/**
 * Sanitizes filter objects by removing null, undefined, and empty array fields.
 * This is useful for cleaning up filter objects before sending them to APIs
 * or storing them in state, ensuring only meaningful filter values are included.
 *
 * @param filters - The filter object to sanitize
 * @returns A new object with null, undefined, and empty array fields removed
 */

export const sanitizeFilters = <T extends Record<string, any>>(
  filters: T
): Partial<T> => {
  const sanitized: Partial<T> = {};

  for (const [key, value] of Object.entries(filters)) {
    // Skip null and undefined values
    if (value === null || value === undefined) {
      continue;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }

    // Skip empty strings (optional - uncomment if needed)
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }

    // For nested objects, recursively sanitize them
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const sanitizedNested = sanitizeFilters(value);
      // Only include if the nested object has at least one property
      if (Object.keys(sanitizedNested).length > 0) {
        sanitized[key as keyof T] = sanitizedNested as T[keyof T];
      }
    } else {
      sanitized[key as keyof T] = value;
    }
  }

  return sanitized;
};
